import React, { FC, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import VerticalTabs from 'src/components/customer-admin/equipments/VerticalTabs'
import { useGetMainComponentTypeWithMainComponentCount } from 'src/api/main-component-type'
import { useParams } from 'react-router-dom'
import { EquipmentIcon } from 'src/components/customer-admin/equipments/EquipmentIcon'
import { MainComponentAddContainer } from 'src/screens/customer/mainComponents/MainComponentAddContainer'
import { useGetDiscardedMainComponents } from 'src/api/main-component'
import { IMainComponentType } from 'src/enums'
import { DiscardedMainComponentList } from 'src/screens/customer/mainComponents/DiscardedMainComponentList'
import EquipmentListContainer from './EquipmentListContainer'
import { MainComponentFilters } from 'src/generated-types/main-component'

interface EquipmentCategoryContainerProps {
  searchText?: string
  onHeaderFiltersChange?: (filters: MainComponentFilters) => void
  headerFilters?: MainComponentFilters
  searchField?: React.ReactNode
}

const priorityOrder = [IMainComponentType.Ring, IMainComponentType.Net, IMainComponentType.LiceSkirt]

// Hide the combined "All" equipment tab; only type-specific tabs are shown. Flip to re-enable.
const SHOW_ALL_EQUIPMENT_TAB = false

const EquipmentCategoryContainer: FC<EquipmentCategoryContainerProps> = ({
  searchText,
  onHeaderFiltersChange,
  headerFilters,
  searchField,
}) => {
  const { id: customerId } = useParams()
  const { t: translate, i18n } = useTranslation(['main-component-type', 'common', 'main-component'])
  const { data: mainComponentTypesWithCounts, isLoading: loading } = useGetMainComponentTypeWithMainComponentCount(
    customerId as EntityId,
    searchText,
  )
  const { data: discardedComponents } = useGetDiscardedMainComponents(customerId as EntityId, searchText)
  const currentLanguage = i18n.language

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0)
  const [totalEquipmentCount, setTotalEquipmentCount] = useState<number>(0)

  useEffect(() => {
    if (SHOW_ALL_EQUIPMENT_TAB && searchText != '') setSelectedTabIndex(0)
  }, [searchText])

  const sortedMainComponentTypes = React.useMemo(() => {
    if (!mainComponentTypesWithCounts) return []

    return [...mainComponentTypesWithCounts].sort((a, b) => {
      const aPriority = priorityOrder.indexOf(a.componentType)
      const bPriority = priorityOrder.indexOf(b.componentType)

      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority
      if (aPriority !== -1) return -1
      if (bPriority !== -1) return 1

      const aNameObj = JSON.parse(a.mainComponentTypeName)
      const bNameObj = JSON.parse(b.mainComponentTypeName)
      const aLabel = aNameObj[currentLanguage] || aNameObj.en
      const bLabel = bNameObj[currentLanguage] || bNameObj.en

      return aLabel.localeCompare(bLabel)
    })
  }, [mainComponentTypesWithCounts, currentLanguage])

  const [mappedArray, setMappedArray] = useState<any>()

  useEffect(() => {
    if (!loading) {
      setMappedArray(
        sortedMainComponentTypes.map((item: any, index: number) => {
          const nameObj = JSON.parse(item.mainComponentTypeName)
          return {
            label: nameObj[currentLanguage] || nameObj.en,
            content: (
              <MainComponentAddContainer
                mainComponentTypeName={item.mainComponentTypeName}
                componentTypeId={item.mainComponentTypeId}
                itemCount={item?.mainComponentsCount ?? 0}
                searchText={searchText}
                assignedCustomerId={item.customerId}
                searchField={searchField}
              />
            ),
            icon: (
              <EquipmentIcon
                type={item.componentType}
                isWhite={selectedTabIndex === index + (SHOW_ALL_EQUIPMENT_TAB ? 1 : 0)}
                height={17}
                width={17}
              />
            ),
            count: item.mainComponentsCount,
            type: item.componentType,
            isDiscarded: false,
          }
        }),
      )
    }
  }, [loading, sortedMainComponentTypes, selectedTabIndex, searchText, searchField])

  const equipmentTabs = React.useMemo(
    () => [
      ...(SHOW_ALL_EQUIPMENT_TAB
        ? [
            {
              label: `${translate('all', { ns: 'common' })}`,
              content: (
                <EquipmentListContainer
                  customerId={customerId as EntityId}
                  searchText={searchText}
                  onHeaderFiltersChange={onHeaderFiltersChange ?? (() => {})}
                  headerFilters={headerFilters ?? {}}
                  onTotalCountChange={setTotalEquipmentCount}
                />
              ),
              icon: (
                <EquipmentIcon
                  type={IMainComponentType.Custom}
                  isWhite={selectedTabIndex === 0}
                  height={17}
                  width={17}
                />
              ),
              count: totalEquipmentCount,
              type: IMainComponentType.Custom,
              isDiscarded: false,
            },
          ]
        : []),
      ...(mappedArray ? mappedArray : []),
      ...(discardedComponents
        ? [
            {
              label: `${translate('discarded', { ns: 'main-component' })}`,
              content: <DiscardedMainComponentList customerId={customerId as EntityId} search={searchText} />,
              icon: (
                <EquipmentIcon
                  type={IMainComponentType.Custom}
                  isWhite={selectedTabIndex === sortedMainComponentTypes.length + (SHOW_ALL_EQUIPMENT_TAB ? 1 : 0)}
                  height={17}
                  width={17}
                />
              ),
              count: discardedComponents.totalCount ?? 0,
              type: IMainComponentType.Custom,
              isDiscarded: true,
            },
          ]
        : []),
    ],
    [
      sortedMainComponentTypes,
      totalEquipmentCount,
      discardedComponents,
      selectedTabIndex,
      currentLanguage,
      translate,
      customerId,
      searchText,
      loading,
      mappedArray,
      onHeaderFiltersChange,
      headerFilters,
    ],
  )

  return (
    <Box className="bg-white" width="100%" minHeight={window.innerHeight - 160}>
      <VerticalTabs tabs={equipmentTabs} selectedIndex={selectedTabIndex} onTabChange={setSelectedTabIndex} />
    </Box>
  )
}

export default EquipmentCategoryContainer
